<template>
    <form-wrapper @form-submit="submit">
        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.first_name.label')"
                    :placeholder="$t('forms.user.profile.fields.first_name.placeholder')"
                    :isRequired="true"
                    :error="data.firstName ? undefined : ' '"
                    type="text"
                    name="firstName"
                    valuePath="firstName"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.last_name.label')"
                    :placeholder="$t('forms.user.profile.fields.last_name.placeholder')"
                    :isRequired="true"
                    :error="data.lastName ? undefined : ' '"
                    type="text"
                    name="lastName"
                    valuePath="lastName"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.facebook.label')"
                    :placeholder="$t('forms.user.profile.fields.facebook.placeholder')"
                    type="text"
                    valuePath="facebook"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.instagram.label')"
                    :placeholder="$t('forms.user.profile.fields.instagram.placeholder')"
                    type="text"
                    valuePath="instagram"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.linkedin.label')"
                    :placeholder="$t('forms.user.profile.fields.linkedin.placeholder')"
                    type="text"
                    valuePath="linkedIn"
                    @change="change"
                ></text-input>
            </form-cell>

            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.twitter.label')"
                    :placeholder="$t('forms.user.profile.fields.twitter.placeholder')"
                    type="text"
                    valuePath="twitter"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half">
                <text-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.tiktok.label')"
                    :placeholder="$t('forms.user.profile.fields.tiktok.placeholder')"
                    type="text"
                    valuePath="tiktok"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell>
                <textarea-input
                    :formData="data"
                    :label="$t('forms.user.profile.fields.description.label')"
                    :placeholder="$t('forms.user.profile.fields.description.placeholder')"
                    valuePath="description"
                    :showInfoTip="true"
                    :maxlength="600"
                    :infoTipMessage="$t('forms.user.profile.fields.description.description')"
                    @change="change"
                ></textarea-input>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button
                    type="primary"
                    :isProcessing="isLoading"
                    :label="$t('forms.user.profile.buttons.save')"
                ></common-button>
            </common-buttons>
            <common-yes-no-modal
                v-if="isLocationYesNoModalOpen || isMobileYesNoModalOpen"
                :showWarningIcon="true"
                :headingText="yesNoHeaderText"
                :noButtonText="$t('common.microcopy.false')"
                :yesButtonText="$t('common.microcopy.true')"
                height="tiny"
                @close="closeYesNoModal"
                @continue="openNextTab"
            ></common-yes-no-modal>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import UsersAPI from 'api/UsersAPI'
    import RoutesEnum from 'enums/routes.enum'
    import SocialMediaEnums from 'enums/social_media.enum'
    import { getSocialMediaURL } from '~/helpers'

    export default {
        mixins: [FormMixin],

        props: {
            userLocations: { type: Array, required: true },
        },

        data() {
            return {
                user: null,
                isLocationYesNoModalOpen: false,
                isMobileYesNoModalOpen: false,
                yesNoHeaderText: '',
            }
        },

        methods: {
            submit() {
                const socialMediaPaths = {}
                getSocialMediaURL(this.data, socialMediaPaths, SocialMediaEnums)

                const updatedProfileInfo = {
                    firstName: this.data.firstName,
                    lastName: this.data.lastName,
                    mobile: this.data.mobile,
                    description: this.data.description ?? null,
                    ...socialMediaPaths,
                }

                this.submitForm(UsersAPI.updateProfile(updatedProfileInfo))
                    .then(() => {
                        this.$auth.reloadUser()
                        this.$toast.success(this.$t('common.microcopy.success'))
                        this.checkPersonalLocationData()
                    })
                    .catch(() => {})
            },

            checkPersonalLocationData() {
                if (!this.user?.mobile?.length) {
                    this.yesNoHeaderText = this.$t('forms.user.profile.messages.mobile_not_set')
                    this.isMobileYesNoModalOpen = true
                    return
                }
                if (!this.userLocations?.length && !this.user.companyUsers?.length) {
                    this.yesNoHeaderText = this.$t('forms.user.profile.messages.locations_not_set')
                    this.isLocationYesNoModalOpen = true
                    return
                }
                this.$router.push({ name: RoutesEnum.INDEX.HOME })
            },

            openNextTab() {
                if (this.isLocationYesNoModalOpen) {
                    this.isLocationYesNoModalOpen = false
                    this.emitEvent({ name: 'open-location-tab' })
                    return
                }
                if (this.isMobileYesNoModalOpen) {
                    this.isMobileYesNoModalOpen = false
                    this.emitEvent({ name: 'open-personal-details-tab' })
                    return
                }
            },

            closeYesNoModal() {
                this.isLocationYesNoModalOpen = false
                this.isMobileYesNoModalOpen = false
            },
        },

        mounted() {
            this.user = this.$auth.getUser()
            this.user.description = this.user?.descriptionTextContent?.originalText
            this.prefillData(this.user)
        },
    }
</script>

<style lang="scss" scoped>
    .credential-buttons-form-cell {
        align-self: flex-end;
    }
    .credential-buttons {
        height: 100%;
        display: flex;
        flex-direction: column;
        button {
            margin: 2px 0;
        }
    }
</style>
